import { Navigate } from "react-router-dom";

import { useAuth } from "@/shared/contexts/AuthProvider";

import { NAV_SECTIONS } from "./nav-config";

/**
 * Index-route (`/`) destination, permission-aware.
 *
 * The planner route is gated, so a blanket `/ → /planner` redirect dumps users
 * who only have maintenance/report access straight onto an access-denied page.
 * Instead, send them to the first primary nav destination they can actually
 * open (nav order = priority). If they can open none, show a neutral message
 * rather than redirecting into a gated route. Permissions are guaranteed loaded
 * by the time this renders (AuthProvider holds the app until then).
 */
export function LandingRedirect() {
  const { can } = useAuth();

  const target = NAV_SECTIONS.flatMap((section) => section.items).find(
    (leaf) =>
      !leaf.disabled &&
      leaf.requiredPermission != null &&
      can(leaf.requiredPermission),
  );

  if (target) {
    return <Navigate to={target.to} replace />;
  }

  return (
    <div
      className="flex min-h-[60vh] items-center justify-center px-6"
      data-testid="no-accessible-pages"
    >
      <div className="w-full max-w-md rounded-2xl border border-border-primary bg-background-card p-8 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-text-primary">
          No pages available
        </h1>
        <p className="mt-2 text-sm text-text-secondary">
          Your account doesn&apos;t have access to any pages yet. Ask an
          administrator to grant access.
        </p>
      </div>
    </div>
  );
}
